import { useRef } from "react";
import { ImagePlus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useObjectUrl } from "./useObjectUrl";

export function FrameInput({
  id,
  label,
  file,
  onChange,
  required,
}: {
  id: string;
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
  required?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const preview = useObjectUrl(file);
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required ? <span className="text-destructive">*</span> : null}
      </Label>
      <div className="flex min-h-24 w-full items-center gap-3 rounded-lg border border-dashed bg-muted/30 p-3 transition-colors hover:bg-muted/60">
        <Label
          htmlFor={id}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left focus-within:ring-2 focus-within:ring-ring"
        >
          {preview ? (
            <img
              src={preview}
              alt=""
              className="size-16 rounded-md object-cover"
            />
          ) : (
            <span className="grid size-16 place-items-center rounded-md bg-muted">
              <ImagePlus className="size-5 text-muted-foreground" />
            </span>
          )}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">
              {file?.name ?? "Choose an image"}
            </span>
            <span className="text-xs text-muted-foreground">
              JPEG, PNG, or WebP · up to 25 MB
            </span>
          </span>
        </Label>
        {file ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove ${label.toLowerCase()}`}
            onClick={(event) => {
              event.stopPropagation();
              onChange(null);
              if (input.current) input.current.value = "";
            }}
          >
            <Trash2 className="size-4" />
          </Button>
        ) : null}
      </div>
      <input
        ref={input}
        id={id}
        className="sr-only"
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(event) => onChange(event.target.files?.[0] ?? null)}
      />
    </div>
  );
}
